import { Injectable, Injector } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AuthService } from "./auth.service";
import { ConfigService } from "./config.service";
import { CommonService } from "./common.service";
import { CookieService } from "./cookie";

/**
 * Sends error details to the Sahel backend via the channel API.
 *
 * Captures errors from two sources:
 *  1. BaseClassService HTTP interceptor (call capture() directly)
 *  2. window.onerror for unhandled JS exceptions (registered in constructor)
 *
 * All methods are fire-and-forget — errors during error capture must never
 * affect the running application.
 */
@Injectable({ providedIn: "root" })
export class ErrorLoggingService {
  private readonly ENDPOINT = "ErrorCapture/CaptureError";

  constructor(private injector: Injector) {
    // Register window.onerror for unhandled JS exceptions
    window.addEventListener("error", (event: ErrorEvent) => {
      this.capture({ errorCode: "ERR-WINDOW", message: event.message }, "UnhandledException");
    });
  }

  /** Call this from BaseClassService HTTP interceptor on HTTP errors. */
  capture(error: any, stage: string = "Unknown"): void {
    this.send(this.buildPayload(error, stage));
  }

  /** Call this from BaseClassService HTTP interceptor on HTTP errors. */
  captureHttpError(error: any, stage: string): void {
    this.send(this.buildPayload(error, stage));
  }

  /** Convenience for window.onerror. */
  captureUnhandledException(event: ErrorEvent): void {
    this.send(this.buildPayload({ errorCode: "ERR-WINDOW", message: event.message }, "UnhandledException"));
  }

  private send(payload: object): void {
    try {
      const config: ConfigService = this.injector.get(ConfigService);
      const baseUrl = config.server;
      if (!baseUrl) {
        console.warn("[ErrorLogging] config.server not set — skipping");
        return;
      }
      const http: HttpClient = this.injector.get(HttpClient);
      http.post(baseUrl + this.ENDPOINT, payload, {
        headers: new HttpHeaders({ "Content-Type": "application/json" }),
      }).subscribe({
        next: () => console.debug("[ErrorLogging] captured:", (payload as any)["errorCode"]),
        error: (err) => console.warn("[ErrorLogging] send failed:", err),
      });
    } catch (e) {
      console.warn("[ErrorLogging] error in send:", e);
    }
  }

  private buildPayload(error: any, stage: string): object {
    let auth: AuthService;
    let cookieService: CookieService;
    try {
      auth = this.injector.get(AuthService);
      cookieService = this.injector.get(CookieService);
    } catch {
      auth = null as any;
      cookieService = null as any;
    }

    const deviceId = cookieService ? cookieService.get("deviceid", false) ?? "" : "";
    const userId = auth?.detailuser?.userId ?? auth?.mobileNumber ?? "";
    const mobile = auth?.detailuser?.mobileNumber ?? auth?.mobileNumber ?? "";
    const cpr = this.getCprFromUser(auth);
    const sessionId = auth?.accessToken ?? "";

    let errorCode = "ERR-0000";
    let errorMessage = "";

    if (typeof error === "string") {
      errorMessage = error;
    } else if (error?.errorCode) {
      errorCode = error.errorCode;
      errorMessage = error.message ?? "";
    } else if (error?.status) {
      errorCode = `ERR-HTTP-${error.status}`;
      errorMessage = error?.statusText ?? error?.message ?? "";
    } else if (error?.message) {
      errorMessage = error.message;
    } else {
      errorMessage = JSON.stringify(error);
    }

    return {
      errorCode,
      errorMessage,
      stage,
      customerCPR: cpr,
      customerName: auth?.detailuser?.firstName
        ? `${auth.detailuser.firstName} ${auth.detailuser.lastName ?? ""}`.trim()
        : "",
      customerMobile: mobile,
      applicationRef: "",
      productLine: "",
      deviceType: "Web",
      oSVersion: navigator.platform ?? "",
      networkType: "",
      sessionId,
      screenshotUrl: "",
      channelId: 3,
    };
  }

  private getCprFromUser(auth: AuthService | null): string {
    if (!auth) return "";
    const reg = (auth as any)?.registrationObjects;
    return reg?.bvn ?? "";
  }
}
